import { useRef, useEffect, type KeyboardEvent, type ClipboardEvent, type InputHTMLAttributes } from 'react';

import styles from './OTPInput.module.css';

export interface OtpInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  error?: string;
  label?: string;
}

const OtpInput = ({
  length = 6,
  value,
  onChange,
  onBlur,
  hasError = false,
  error,
  label,
  disabled = false,
  className = '',
  id,
  ...rest
}: OtpInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const chars = value.split('').slice(0, length);
    inputsRef.current.forEach((input, index) => {
      if (input) {
        input.value = chars[index] || '';
      }
    });
  }, [value, length]);

  const focusInput = (index: number) => {
    const input = inputsRef.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const digit = event.target.value.replace(/\D/g, '').slice(-1);
    if (!digit) return;

    const newValue = value.slice(0, index) + digit + value.slice(index + 1);
    onChange(newValue.slice(0, length));

    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (value[index]) {
        const newValue = value.slice(0, index) + value.slice(index + 1);
        onChange(newValue);
      } else if (index > 0) {
        focusInput(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
      return;
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    focusInput(Math.min(pasted.length, length - 1));
  };

  const isInvalid = hasError || !!error;

  return (
    <div className={`${styles.otpField} ${className}`.trim()}>
      {label && (
        <label htmlFor={id} className={styles.otpLabel}>
          {label}
        </label>
      )}
      <div
        className={`${styles.otpInputs} ${isInvalid ? styles.hasError : ''}`.trim()}
        role="group"
        aria-label="One-time password"
      >
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            id={index === 0 ? id : undefined}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            disabled={disabled}
            aria-label={`Digit ${index + 1}`}
            className={styles.otpDigit}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            onBlur={index === length - 1 ? onBlur : undefined}
            {...rest}
          />
        ))}
      </div>
      {error && <div className={styles.otpError}>{error}</div>}
    </div>
  );
};

export default OtpInput;
